import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useSession } from '../auth/session'
import { Permissions } from '../auth/permissions'
import { initials } from '../lib/format'

/** Who is signed in, top right: their name and role, their approval PIN, and signing out. */
export function UserMenu() {
  const user = useSession((s) => s.user)
  const can = useSession((s) => s.can)
  const signOut = useSession((s) => s.signOut)
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const outside = (event: PointerEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false)
    }
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', outside)
    window.addEventListener('keydown', escape)
    return () => {
      document.removeEventListener('pointerdown', outside)
      window.removeEventListener('keydown', escape)
    }
  }, [open])

  if (!user) return null

  // Only people who can approve at a till need a PIN.
  const canApprove = [Permissions.Sales.ApproveDiscount, Permissions.Sales.OverrideMinimumPrice, Permissions.Sales.OverrideCreditLimit].some((p) => can(p))

  return (
    <div ref={box} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account: ${user.name}`}
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2.5 rounded-lg py-1 pl-1 pr-2 hover:bg-dpl-bg"
      >
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-dpl-nav text-sm font-semibold text-white">{initials(user.name)}</span>
        <span className="hidden text-left leading-tight md:block">
          <span className="block text-sm font-semibold text-dpl-text">{user.name}</span>
          <span className="block text-xs text-dpl-muted">{user.role}</span>
        </span>
        <svg viewBox="0 0 20 20" className="hidden h-4 w-4 text-dpl-muted md:block" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
          <path d="m5 7.5 5 5 5-5" />
        </svg>
      </button>
      {open && (
        <div role="menu" aria-label="Account" className="absolute right-0 z-40 mt-2 w-60 rounded-lg border border-dpl-border bg-dpl-surface py-1.5 shadow-lg">
          <div className="border-b border-dpl-border px-4 pb-2.5 pt-1 md:hidden">
            <p className="text-sm font-semibold">{user.name}</p>
            <p className="text-xs text-dpl-muted">{user.role}</p>
          </div>
          {user.locationName && <p className="px-4 py-2 text-xs text-dpl-muted">{user.locationName}</p>}
          {canApprove && (
            <Link role="menuitem" to="/account/pin" onClick={() => setOpen(false)} className="block px-4 py-2 text-sm hover:bg-dpl-bg">
              {user.hasPin ? 'Approval PIN' : 'Set your approval PIN'}
            </Link>
          )}
          <Link role="menuitem" to="/account/two-factor" onClick={() => setOpen(false)} className="block px-4 py-2 text-sm hover:bg-dpl-bg">
            Two-factor sign-in {user.twoFactorEnabled ? <span className="text-xs text-dpl-success">· on</span> : <span className="text-xs text-dpl-muted">· off</span>}
          </Link>
          <Link role="menuitem" to="/account/shop-cover" onClick={() => setOpen(false)} className="block px-4 py-2 text-sm hover:bg-dpl-bg">
            Working at another shop
          </Link>
          <button role="menuitem" type="button" onClick={() => void signOut()} className="block w-full px-4 py-2 text-left text-sm text-dpl-danger hover:bg-dpl-bg">
            Sign out
          </button>
        </div>
      )}
    </div>
  )
}
